import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Package,
  ShoppingBag,
  Clock,
  Wallet,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';

export function DashboardHome() {
  const { t } = useLanguage();
  const [productsCount, setProductsCount] = useState(0);
  const [ordersCount, setOrdersCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [recentOrders, setRecentOrders] = useState<any[]>([]);

  useEffect(() => {
    supabase
      .from('products')
      .select('id', { count: 'exact', head: true })
      .then(({ count }) => setProductsCount(count ?? 0));
    supabase
      .from('orders')
      .select('id', { count: 'exact', head: true })
      .then(({ count }) => setOrdersCount(count ?? 0));
    supabase
      .from('orders')
      .select('id', { count: 'exact', head: true })
      .in('status', ['confirmed', 'preparing', 'shipped'])
      .then(({ count }) => setInProgressCount(count ?? 0));
    supabase
      .from('orders')
      .select('id, order_number, status, total_amount, currency, created_at')
      .in('status', ['confirmed', 'preparing', 'shipped'])
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data }) => setRecentOrders(data ?? []));
  }, []);

  const stats = [
    {
      title: t('business.products'),
      value: String(productsCount),
      desc: 'Catalogue',
      icon: Package,
      gradient: 'from-emerald-600/90 via-teal-600/85 to-cyan-700/80',
      iconClass: 'text-white',
    },
    {
      title: t('business.orders'),
      value: String(ordersCount),
      desc: 'Total commandes',
      icon: ShoppingBag,
      gradient: 'from-blue-600/90 via-indigo-600/85 to-violet-700/80',
      iconClass: 'text-white',
    },
    {
      title: t('business.orders_in_progress'),
      value: String(inProgressCount),
      desc: 'À traiter',
      icon: Clock,
      gradient: 'from-amber-500/90 via-orange-500/85 to-rose-600/80',
      iconClass: 'text-white',
    },
  ];

  const shortcuts = [
    { to: '/espace-business/products', labelKey: 'business.products', icon: Package },
    { to: '/espace-business/orders', labelKey: 'business.orders', icon: ShoppingBag },
    { to: '/espace-business/in-progress', labelKey: 'business.orders_in_progress', icon: Clock },
    { to: '/espace-business/settings', labelKey: 'business.settings', icon: Wallet },
  ];

  return (
    <div className="flex flex-1 flex-col gap-6">
      {/* En-tête */}
      <div className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8">
        <div className="flex flex-col gap-1">
          <p className="flex items-center gap-2 font-['Rubik'] text-sm font-medium text-white/60">
            <Sparkles className="size-4 text-amber-400" />
            Vue d’ensemble
          </p>
          <h2 className="font-['Sora'] text-2xl font-semibold tracking-tight text-white md:text-3xl">
            {t('business.dashboard')}
          </h2>
          <p className="max-w-xl font-['Rubik'] text-sm text-white/50">
            Résumé de votre activité e‑commerce Maison Adji.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((item) => (
          <Card
            key={item.title}
            className={`overflow-hidden border-0 bg-gradient-to-br ${item.gradient} shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl`}
          >
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white/90">
                {item.title}
              </CardTitle>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                <item.icon className={`size-5 ${item.iconClass}`} />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-['Sora'] text-2xl font-bold tracking-tight text-white md:text-3xl">
                {item.value}
              </p>
              <p className="mt-1 font-['Rubik'] text-xs text-white/80">{item.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Raccourcis */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {shortcuts.map((s) => (
          <Link key={s.to} to={s.to}>
            <Card className="h-full transition-all duration-200 hover:border-white/20 hover:bg-white/10">
              <CardContent className="flex flex-row items-center gap-4 p-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white/80">
                  <s.icon className="size-6" />
                </div>
                <div className="flex-1">
                  <p className="font-['Sora'] font-medium text-white">{t(s.labelKey)}</p>
                  <p className="font-['Rubik'] text-xs text-white/50">Accéder</p>
                </div>
                <ArrowRight className="size-4 text-white/40" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Commandes en cours */}
      <Card>
        <CardHeader className="border-b border-white/10">
          <CardTitle>{t('business.orders_in_progress')}</CardTitle>
          <CardDescription>Dernières commandes à traiter.</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {recentOrders.length === 0 ? (
            <p className="font-['Rubik'] text-white/50">Aucune commande en cours.</p>
          ) : (
            <ul className="space-y-3">
              {recentOrders.map((o) => (
                <li
                  key={o.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/5 px-4 py-3"
                >
                  <span className="font-['Rubik'] font-medium text-white">
                    {o.order_number || o.id.slice(0, 8)}
                  </span>
                  <span className="font-['Rubik'] text-sm text-white/60">{o.status}</span>
                  <span className="font-['Sora'] text-white">
                    {Number(o.total_amount).toLocaleString()} {o.currency || 'XOF'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/espace-business/in-progress"
            className="mt-4 inline-flex items-center gap-2 font-['Rubik'] text-sm text-white/70 hover:text-white"
          >
            Voir tout <ArrowRight className="size-4" />
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
